import { useCallback } from "react";
import { UseFormGetValues } from "react-hook-form";
import { format } from "date-fns";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import {
  IRecurringConflictItem,
  conflictDayNames,
} from "../../common/recurringEventSelector/models/recurringEventModels";
import { IClassWizardFormModel } from "../models/classWizardFormModel";

/** An existing class the new one would duplicate; times are studio-local, no offset. */
interface IClassConflictModel {
  name: string;
  startTime: string;
  endTime: string;
}

const toConflictItem = (conflict: IClassConflictModel): IRecurringConflictItem => {
  const start = new Date(conflict.startTime);

  return {
    date: format(start, "dd.MM.yyyy"),
    dayName: conflictDayNames[start.getDay()],
    title: conflict.name,
    startTime: format(start, "HH:mm"),
    endTime: format(new Date(conflict.endTime), "HH:mm"),
    conflictType: "sameName",
  };
};

/**
 * The class wizard's "check for conflicts": asks the server which occurrences of the new
 * class duplicate an existing class (same name and start). The instructor's other bookings
 * don't count, so simultaneous classes with one instructor are allowed. The server builds
 * the series itself, exactly as it will create it.
 */
export const useClassConflictsCheck = (
  getValues: UseFormGetValues<IClassWizardFormModel>,
) => {
  const { mutateAsync } = useApiMutation<IClassConflictModel[]>();

  return useCallback(async (): Promise<IRecurringConflictItem[]> => {
    const values = getValues();

    const conflicts = await mutateAsync({
      endpoint: endpointsMetadata.planner.classConflicts,
      method: "post",
      payload: {
        studioId: values.studioId,
        name: values.name,
        type: values.type,
        // The wall-clock time picked, which the server reads as studio time (as on save),
        // to the millisecond: the server matches starts exactly.
        date: format(values.date, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
        durationInMinutes: values.durationInMinutes,
        isRecurring: values.isRecurring,
        recurringDays: values.recurringDays,
        recurringPeriod: values.recurringPeriod,
        recurringPeriodType: values.recurringPeriodType,
        recurringInterval: values.recurringInterval,
        recurringIntervalType: values.recurringIntervalType,
      },
      globalIsLoading: false,
      disableSuccessToast: true,
    });

    return (conflicts ?? []).map(toConflictItem);
  }, [getValues, mutateAsync]);
};
